import { ThemeService } from './../../_helpers/theming-engine/theme.service';
import { PlacementMaintenance } from './../../_models/maintenance/placement-maintenance.model';
import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { PlacementService } from './../../_services';
import { ClientService } from './../../_services/client.service';
import { ClientUserDetails } from './../../_models/maintenance/client-user-details.model';
import { ErrorModalHelperService } from '../../_helpers/error-modal-helper/error-modal-helper.service';
import { SuccessModalHelperService } from '../../_helpers/success-modal-helper/success-modal-helper.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { WhoCanSeeBookingComponent } from '../who-can-see-booking/who-can-see-booking.component';

@Component({
    selector: 'student-group-maintenance',
    templateUrl: './student-group-maintenance.component.html',
    styleUrls: ['./student-group-maintenance.component.css'],
    standalone: false
})
export class StudentGroupMaintenanceComponent implements OnInit {

  public frmGroupMaintenance: UntypedFormGroup;
  public custodianList: Array<ClientUserDetails> = new Array<ClientUserDetails>();

  public placementId: string = "";
  public clientName: string = "";
  public clientId: number = 0;
  public ownerName: string = "";

  /** Who can see this booking: a standalone section, hosted with ngComponentOutlet. It shows itself only while its
   * switch is on and the user holds its role feature. */
  public readonly whoCanSee = WhoCanSeeBookingComponent;

  public startDate: Date = new Date("01/01/2020");
  public endDate: Date = new Date("03/01/2020");

  private dbMaintenanceObject: PlacementMaintenance;
  private savedChanges: boolean = false;

  public compareCustodian = (a: any, b: any): boolean => a != null && b != null && String(a) === String(b);

  constructor(
    private formBuilder: UntypedFormBuilder,
    public themeService: ThemeService,
    private clientService: ClientService,
    private placementService: PlacementService,
    private successModalHelper: SuccessModalHelperService,
    private errorModalHelper: ErrorModalHelperService,
    private dialogRef: MatDialogRef<StudentGroupMaintenanceComponent>,
    @Inject(MAT_DIALOG_DATA) private data: { placementId: string | number; externalView?: boolean; placementMaintenance?: PlacementMaintenance; clientUserDetails?: ClientUserDetails[] },
  ) {
    this.placementId = String(this.data.placementId);
  }

  ngOnInit() {
    this.initializeFormGroup();

    if (this.data.placementMaintenance && this.data.clientUserDetails) {
      this.applyLoadedData(this.data.placementMaintenance, this.data.clientUserDetails);
    } else {
      this.getPlacementMaintenanceInfo();
    }
  }

  private initializeFormGroup(): void {
    this.frmGroupMaintenance = this.formBuilder.group({
      placementCustodian: [null, Validators.compose([Validators.required, Validators.min(1)])]
    });
  }

  private getPlacementMaintenanceInfo(): void {
    this.placementService.getPlacementMaintenanceInfo(this.placementId).subscribe((placementMaintenance: PlacementMaintenance) => {
      if (placementMaintenance != null && placementMaintenance.placementInfo != null) {
        // With the booking id, the API lists only the custodians who can see this booking.
        this.clientService.getClientUserDetailsByClientId(placementMaintenance.placementInfo.clientId, this.placementId).subscribe((response: ClientUserDetails[]) => {
          this.applyLoadedData(placementMaintenance, response);
        }, error => {
          console.error(error);
          this.errorModalHelper.show("An error has occurred. Please try again or contact your administrator.", error);
        });
      } else {
        console.error("Placement Maintenance object cannot be null.");
        this.errorModalHelper.show("An error has occurred. Please try again or contact your administrator.", { isValidationError: false });
      }
    }, error => {
      console.error(error);
      this.errorModalHelper.show("An error has occurred. Please try again or contact your administrator.", error);
    });
  }

  private applyLoadedData(placementMaintenance: PlacementMaintenance, clientUserDetails: ClientUserDetails[]): void {
    this.initializeFormGroup();

    this.custodianList = clientUserDetails.filter(c => c.isCustodian);
    this.custodianList.forEach(i => i.dropdownDisplay = i.firstName + " " + i.lastName);

    // The API sends the owner's name. placementReferrer is an Orbit contact id, not a user id, so it must
    // never be looked up as a user: that 404s, or names whichever user happens to share the number.
    this.ownerName = placementMaintenance.placementInfo.placementReferrerName || "";

    this.dbMaintenanceObject = placementMaintenance;
    this.populateForm(placementMaintenance).then(() => {
      this.frmGroupMaintenance.markAsPristine();
    });
  }

  private populateForm(placementMaintenance: PlacementMaintenance): Promise<void> {
    return new Promise(resolve => {
      this.frmGroupMaintenance.get("placementCustodian").setValue(placementMaintenance.placementInfo.placementCustodian);

      this.startDate = placementMaintenance.placementInfo.startDate;
      this.endDate = placementMaintenance.placementInfo.endDate;
      this.clientName = placementMaintenance.placementInfo.clientName;
      this.clientId = placementMaintenance.placementInfo.clientId;

      resolve();
    });
  }

  public saveGroupMaintenance(): void {
    this.frmGroupMaintenance.markAllAsTouched();

    if (this.frmGroupMaintenance.invalid) {
      this.errorModalHelper.show("Validation errors found, please complete the highlighted fields", { isValidationError: true }, "Amendments Required");
      return;
    }

    let request = this.dbMaintenanceObject;
    let placementInfo = this.dbMaintenanceObject.placementInfo;
    if (placementInfo.lessonPlanTypeCode == null) {
      placementInfo.lessonPlanTypeCode = "";
    }
    Object.keys(this.frmGroupMaintenance.value)
      .forEach(key => {
        if (placementInfo[key] != undefined) {
          placementInfo[key] = this.frmGroupMaintenance.value[key];
        }
      });

    request.placementInfo = placementInfo;

    this.placementService.updatePlacementMaintenanceInfo(request).subscribe(() => {
      this.dialogRef.close(true);
      this.successModalHelper.show("Successfully saved the " + this.themeService.siteContent.placementHeaderLabel.toLowerCase() + " setup.");
    }, error => {
      this.errorModalHelper.show(error.message, error);
    });
  }

  public close(): void {
    this.dialogRef.close(this.savedChanges);
  }

}
